import { launcherConfig, piiSelector } from './config';
import { SCREEN_MASK_CODE, chordLabel, isChord } from './hotkeys';
import { knownRanges, onKnownChange } from './known';
import { BUG_OWN_ATTR, noteWidgetEvent } from './recorder';

/*
 * Screen mode (report 70; report 184, decision of 29.10 — Quint screen_mask Мс1): while the screen is shared, all content
 * of the page is blurred by the kind of element, not by marks — only headings, buttons, statuses and counters
 * (`SCREEN_OPEN`) stay readable; nodes added later are covered as well, since it is a selector, not a walk. Switched
 * from the help menu, the beacon or Alt+Shift+M when the host sets `launcher.screenMask`.
 *
 * `true` — off until chosen; the choice survives a reload (localStorage, only a flag). `'default-on'` (report 106,
 * decision of 27.10): the page cannot tell that the screen is being shared, so the mode is on at every load; switching
 * it off lives in the tab until a reload and is never stored.
 *
 * Only the page is covered: the widget's own windows (`[data-bug-own]`: report, questions — fields, «по действиям»,
 * thumbnails) stay open (report 109). The shot hides the same nodes (`SCREEN_COVERED`, `capture.ts`, Мс2) on top of
 * the marked names it always hides (Мс3).
 *
 * П600 (reports 70, 47, 109; facts Quint `screen_mask.screenCoversShot`): a name the dictionary knows (`knownRanges`, as
 * `plateKnown` on the shot) is not visible anywhere on the page, the open kinds included — again for every new place
 * (MutationObserver, before the paint) and every new word (`onKnownChange`). Text is covered by a CSS highlight, only the
 * name's range — nothing in the host's DOM changes, the rest of a heading or a button stays readable (Мс1); a field with a
 * known value gets `data-bug-screen-known` and is blurred as content.
 */

export const SCREEN_MASK_CLASS = 'bug-screen-mask';
export const SCREEN_MASK_HOTKEY = chordLabel(SCREEN_MASK_CODE);
const KEY = 'jaioraBugsScreenMask';
const STYLE_ID = 'jaiora-bugs-screen-mask';

/** The choice kept across reloads (`screenMask: true`). */
let stored = read();
/** The choice of this tab (`'default-on'`): forgotten on reload. */
let chosen: boolean | null = null;
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function save(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(KEY, '1');
    else window.localStorage.removeItem(KEY);
  } catch {
  }
}

/** The host offers the mode (`launcher.screenMask`); without it the page is never covered. */
export const screenMaskOffered = (): boolean => {
  const value = launcherConfig()?.screenMask;
  return value === true || value === 'default-on';
};

/** On at every load (`'default-on'`): the tab's choice is not stored. */
export const screenMaskDefaultOn = (): boolean => launcherConfig()?.screenMask === 'default-on';

/** Covered right now. */
export const screenMaskOn = (): boolean => screenMaskOffered() && (screenMaskDefaultOn() ? (chosen ?? true) : stored);

export const SCREEN_KNOWN_ATTR = 'data-bug-screen-known';
export const SCREEN_KNOWN_HIGHLIGHT = 'jaiora-bugs-known';

/**
 * П600 with Мс1 (Quint screen_mask `namesRangeHidden`, `chromeReadable`): a known name is hidden by its own range only — a
 * highlight; the rest of a heading or a button stays readable, no plate on a whole element. A field with such a value is
 * content: its text is blurred like the rest of the content (also inside an open kind). Without highlights in the browser
 * the element of such text is blurred the same way — still no plate.
 */
function knownCss(): string[] {
  const known = `html.${SCREEN_MASK_CLASS} [${SCREEN_KNOWN_ATTR}]`;
  const text = `-webkit-text-fill-color: transparent !important; caret-color: transparent !important; text-shadow: ${SCREEN_BLUR_TEXT} !important;`;
  return [
    `${known}, ${known} * { ${text} }`,
    `${known}::placeholder { ${text} }`,
    // A highlight paints only its own text: no `currentColor` there — the text color of the page's color scheme.
    `::highlight(${SCREEN_KNOWN_HIGHLIGHT}) { color: transparent; -webkit-text-fill-color: transparent; background-color: CanvasText; text-shadow: none; }`,
  ];
}

interface HighlightRegistry {
  set(name: string, highlight: unknown): void;
  delete(name: string): void;
}

const highlights = (): { registry: HighlightRegistry; make: (ranges: AbstractRange[]) => unknown } | null => {
  const registry = (globalThis.CSS as { highlights?: HighlightRegistry } | undefined)?.highlights;
  const Make = (globalThis as { Highlight?: new (...ranges: AbstractRange[]) => unknown }).Highlight;
  return registry && Make ? { registry, make: (ranges) => new Make(...ranges) } : null;
};

/** Text nodes of the page with known words → their ranges (highlight) or the element marked (no highlights). */
const textHits = new Map<Text, Range[]>();
const markedEls = new Set<Element>();
let observer: MutationObserver | null = null;
let stopKnown: (() => void) | null = null;

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE']);

const isOwn = (el: Element): boolean => el.hasAttribute(BUG_OWN_ATTR) || SKIP_TAGS.has(el.tagName);

const pageNode = (node: Node): boolean => {
  const el = node instanceof Element ? node : node.parentElement;
  return !!el && el.isConnected && !el.closest(`[${BUG_OWN_ATTR}]`) && !SKIP_TAGS.has(el.tagName);
};

function mark(el: Element, on: boolean): void {
  if (on) {
    if (!el.hasAttribute(SCREEN_KNOWN_ATTR)) el.setAttribute(SCREEN_KNOWN_ATTR, '');
    markedEls.add(el);
  } else if (markedEls.delete(el)) el.removeAttribute(SCREEN_KNOWN_ATTR);
}

function knownIn(node: Text): Range[] {
  const value = node.nodeValue ?? '';
  if (!/\S/.test(value)) return [];
  return knownRanges(value).map((found) => {
    const range = document.createRange();
    range.setStart(node, found.start);
    range.setEnd(node, found.end);
    return range;
  });
}

/** An element's own text, without highlights: marked while any of its direct text nodes has a known word. */
function scanOwnText(el: Element): void {
  let hit = false;
  for (const child of el.childNodes) {
    if (child.nodeType !== Node.TEXT_NODE) continue;
    const found = knownIn(child as Text);
    if (found.length > 0) {
      textHits.set(child as Text, found);
      hit = true;
    } else textHits.delete(child as Text);
  }
  mark(el, hit);
}

function scanText(node: Text, withHighlights: boolean): void {
  if (!pageNode(node)) {
    textHits.delete(node);
    return;
  }
  if (!withHighlights) {
    if (node.parentElement) scanOwnText(node.parentElement);
    return;
  }
  const found = knownIn(node);
  if (found.length > 0) textHits.set(node, found);
  else textHits.delete(node);
}

/** A field's value is not text of the page: the whole field is blurred (the shot blurs it the same way — `maskClone`, Мс6). */
function scanField(el: HTMLInputElement | HTMLTextAreaElement): void {
  mark(el, pageNode(el) && !!el.value && knownRanges(el.value).length > 0);
}

function scanTree(root: Node, withHighlights: boolean): void {
  if (root.nodeType === Node.TEXT_NODE) {
    scanText(root as Text, withHighlights);
    return;
  }
  if (!(root instanceof Element) || !pageNode(root)) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node instanceof Element && isOwn(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let node: Node | null = walker.currentNode; node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (withHighlights) scanText(node as Text, true);
    } else if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) scanField(node);
    else if (!withHighlights && node instanceof Element) scanOwnText(node);
  }
}

function paintKnown(): void {
  for (const node of textHits.keys()) if (!node.isConnected) textHits.delete(node);
  for (const el of markedEls) if (!el.isConnected) markedEls.delete(el);
  const api = highlights();
  if (!api) return;
  const ranges = [...textHits.values()].flat();
  if (ranges.length === 0) api.registry.delete(SCREEN_KNOWN_HIGHLIGHT);
  else api.registry.set(SCREEN_KNOWN_HIGHLIGHT, api.make(ranges));
}

/** The whole page again: on switching on and after every new word. */
export function rescanScreenKnown(): void {
  if (!observer || typeof document === 'undefined') return;
  textHits.clear();
  for (const el of [...markedEls]) mark(el, false);
  scanTree(document.body, !!highlights());
  paintKnown();
}

function onMutations(records: MutationRecord[]): void {
  const withHighlights = !!highlights();
  for (const record of records) {
    if (record.type === 'characterData') scanText(record.target as Text, withHighlights);
    else {
      for (const node of record.addedNodes) scanTree(node, withHighlights);
      if (!withHighlights && record.target instanceof Element && pageNode(record.target)) scanOwnText(record.target);
    }
  }
  paintKnown();
}

function onFieldInput(event: Event): void {
  const el = event.target;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) scanField(el);
}

function watchKnown(on: boolean): void {
  if (on === !!observer) return;
  if (on) {
    observer = new MutationObserver(onMutations);
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    document.addEventListener('input', onFieldInput, true);
    stopKnown = onKnownChange(rescanScreenKnown);
    rescanScreenKnown();
    return;
  }
  observer?.disconnect();
  observer = null;
  stopKnown?.();
  stopKnown = null;
  document.removeEventListener('input', onFieldInput, true);
  textHits.clear();
  for (const el of [...markedEls]) mark(el, false);
  highlights()?.registry.delete(SCREEN_KNOWN_HIGHLIGHT);
}

/**
 * Readable in screen mode, with everything inside them. Headings; buttons, tabs and menu items; statuses and badges;
 * counters; `[data-bug-open]` — a host's (or a tracker screen's) own mark for chrome the kinds above miss.
 */
export const SCREEN_OPEN = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', '[role="heading"]', 'thead th',
  'button', '[role="button"]', '[role="tab"]', '[role="menuitem"]', 'input[type="button"]', 'input[type="submit"]', 'input[type="reset"]', 'summary', '.btn', '.button',
  '[role="status"]', '[data-status]', '[class*="status"]', '[class*="badge"]',
  '[class*="counter"]', '[class*="-count"]', '[class~="count"]', '[data-count]', '.jui-metric',
  '[data-bug-open]',
].join(', ');

const OPEN_ROOTS = `:is(${SCREEN_OPEN}), [data-bug-own]`;
/** What the mode covers: every element of the page outside the open kinds and the widget's windows. */
export const SCREEN_COVERED_ANY = `body :not(${OPEN_ROOTS}, :is(${OPEN_ROOTS}) *)`;
export const SCREEN_COVERED = `html.${SCREEN_MASK_CLASS} ${SCREEN_COVERED_ANY}`;
const MEDIA = 'img, picture, video, canvas, svg, iframe, object, embed';
/** Blur radius of the text (Мс4: the degree is the owner's call). */
export const SCREEN_BLUR_TEXT = '0 0 0.5em currentColor';
export const SCREEN_BLUR_MEDIA = 'blur(12px)';

/**
 * Text is blurred through its own shadow (the glyphs transparent): blur per text, not per box, so a heading or a button
 * inside a blurred card stays sharp. Both properties inherit, so the open roots take them back, with zero specificity:
 * the page's own values there still win.
 */
export function screenMaskCss(): string {
  const text = `-webkit-text-fill-color: transparent !important; caret-color: transparent !important; text-shadow: ${SCREEN_BLUR_TEXT} !important;`;
  return [
    `${SCREEN_COVERED} { ${text} }`,
    `${SCREEN_COVERED}::placeholder { ${text} }`,
    `${SCREEN_COVERED}:is(${MEDIA}) { filter: ${SCREEN_BLUR_MEDIA} !important; }`,
    `:where(html.${SCREEN_MASK_CLASS} body) :where(${OPEN_ROOTS}) { -webkit-text-fill-color: currentColor; caret-color: auto; text-shadow: none; }`,
    ...markedCss(),
    ...knownCss(),
  ].join('\n');
}

/**
 * Мс5, Мс6 (facts reports 258, 259; Quint screen_marked, screen_one_kind): everything the host marked (`piiSelector()`,
 * the very set the shot hides) is covered on the screen the way the shot covers it — by blur, and inside the open kinds
 * too: a nickname in a list row that is a button, and a marked button itself. Only an unmarked heading or button stays
 * readable (Мс1); the widget's windows stay open.
 */
export const SCREEN_MARKED = (): string => {
  const marked = `html.${SCREEN_MASK_CLASS} body :is(${piiSelector()}):not([data-bug-own], [data-bug-own] *)`;
  return `${marked}, ${marked} *`;
};

function markedCss(): string[] {
  const text = `-webkit-text-fill-color: transparent !important; caret-color: transparent !important; text-shadow: ${SCREEN_BLUR_TEXT} !important;`;
  const marked = SCREEN_MARKED();
  // A marked field shows a name in its hint too (the own-name field with the Telegram name as the hint).
  return [`${marked} { ${text} }`, `:is(${marked})::placeholder { ${text} }`, `:is(${marked}):is(${MEDIA}) { filter: ${SCREEN_BLUR_MEDIA} !important; }`];
}

/** Puts the class and the sheet in line with the choice and the offer. */
export function syncScreenMask(): void {
  if (typeof document === 'undefined') return;
  const on = screenMaskOn();
  document.documentElement.classList.toggle(SCREEN_MASK_CLASS, on);
  if (document.body) watchKnown(on);
  let el = document.getElementById(STYLE_ID);
  if (!on) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = screenMaskCss();
}

export function setScreenMask(on: boolean): void {
  chosen = on;
  if (!screenMaskDefaultOn()) {
    stored = on;
    save(on);
  }
  syncScreenMask();
  noteWidgetEvent(`режим экрана: ${on ? 'вкл' : 'выкл'}`);
  for (const fn of listeners) fn();
}

export const toggleScreenMask = (): void => setScreenMask(!screenMaskOn());

/**
 * Ок6 (Quint report_tools): the shield in the report window's tools row covers the shots, not the page around the panel.
 * Until it is pressed it follows the screen mode (as before); a press makes it the person's own choice for this tab.
 */
let shieldChoice: boolean | null = null;

export const shotShieldOn = (): boolean => shieldChoice ?? screenMaskOn();

export function toggleShotShield(): void {
  shieldChoice = !shotShieldOn();
  noteWidgetEvent(`щит снимков: ${shieldChoice ? 'вкл' : 'выкл'}`);
  for (const fn of listeners) fn();
}

/**
 * What a shot covers now. With the host's switch (`grant`) the shield alone decides (Щт1); without it marked data stays
 * hidden always (Щт2, Мс3) and the content the page covers is covered on the shot as well (Мс2).
 */
export function shotCover(grant: boolean): { hideMarked: boolean; coverScreen: boolean } {
  const shield = shotShieldOn();
  return { hideMarked: !(grant && !shield), coverScreen: grant ? shield : shield || screenMaskOn() };
}

export function onScreenMask(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Alt+Shift+M (by key position: the letter differs per layout); the chord table lives in hotkeys.ts (Кл5). */
export const isScreenMaskKey = (event: KeyboardEvent): boolean => isChord(event, SCREEN_MASK_CODE);

/** Tests: a reload — the tab's choice is gone, the stored flag is read again. */
export function resetScreenMask(): void {
  stored = read();
  chosen = null;
  shieldChoice = null;
  syncScreenMask();
  for (const fn of listeners) fn();
}
